import React, { useEffect, useRef } from 'react';
import styles from './Consultancypage.module.css';

import Consultant from '../../assets/consultencyPic.svg';

// --- Reusable Scroll Reveal Hook ---
const useScrollReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add(styles.animateIn);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return ref;
};

const Consultancypage = () => {
  // Animation Refs
  const heroRef = useScrollReveal();
  const investRef = useScrollReveal();
  const stepsRef = useScrollReveal();
  const expertRef = useScrollReveal();

  return (
    <div className={styles.main}>
      {/* ─── HERO SECTION ─── */}
      <div ref={heroRef} className={`${styles.hero} ${styles.revealScale}`}>
        <span className={styles.heroText}>Find Your <span className={styles.bold}>Perfect Car</span></span> <br />
        <span className={styles.heroText}>with Expert Guidance</span>

        <span className={styles.heroDisc}>
          Get personalized expert guidance from Faisal, India's trusted automotive consultant with 18 years of industry experience. Make the right choice, the first time.
        </span>

        <a href="https://www.caradvice.in/consultancy/" target='_blank' rel="noopener noreferrer" className={styles.ctaBtn}>
          <span className={styles.btnText}>Book a Consultation</span>
          <span className={styles.btnIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </span>
        </a>      
      </div>

      {/* ─── WHY INVEST SECTION ─── */}
      <div ref={investRef} className={styles.revealUp}>
        <div className={styles.titleWrapper}>
          <h2 className={styles.titleText}>Why Invest In Expert Consultation?</h2>
        </div>

        <div className={styles.boxContainer}>
          <div className={styles.card}>
            <span className={styles.watermark}>01</span>
            <h3 className={styles.heading}>Avoid Expensive Mistakes</h3>
            <p className={styles.body}>Buying a car is one of your biggest purchases. A wrong decision can cost you lakhs in depreciation, maintenance, and resale value. Our consultation saves you from costly regrets.</p>
          </div>

          <div className={styles.card}>
            <span className={styles.watermark}>02</span>
            <h3 className={styles.heading}>Cut Through The Confusion</h3>
            <p className={styles.body}>With 400+ car variants in India, overwhelming advertisements, and biased dealership advice, finding the right car feels impossible. We simplify this maze for you.</p>
          </div>

          <div className={styles.card}>
            <span className={styles.watermark}>03</span>
            <h3 className={styles.heading}>Get Unbiased Insights</h3>
            <p className={styles.body}>Unlike dealers pushing inventory or influencers promoting sponsorships, Faisal has zero brand affiliations. Your needs come first, not commissions.</p>
          </div>

          <div className={styles.card}>
            <span className={styles.watermark}>04</span>
            <h3 className={styles.heading}>Save Months Of Research</h3>
            <p className={styles.body}>Why spend countless hours watching reviews, reading forums, and visiting showrooms? Get 18 years of automotive expertise condensed into personalized advice.</p>
          </div>
        </div>
      </div>

      {/* ─── 3 SIMPLE STEPS SECTION ─── */}
      <div ref={stepsRef} className={styles.revealUp}>
        <div className={styles.titleWrapper}>
          <h2 className={styles.titleText}>Your Journey In 3 Simple Steps</h2>
        </div>

        <div className={styles.stepsContainer}>
          <div className={styles.stepCard}>
            <div className={styles.stepNo}>01</div>
            <div className={styles.stepContent}>
              <h3 className={styles.stepHeading}>Book Your Session</h3>
              <p className={styles.stepDisc}>Secure your consultation slot with a simple, transparent payment.</p>
            </div>
          </div>

          <div className={styles.stepCard}>
            <div className={styles.stepNo}>02</div>
            <div className={styles.stepContent}>
              <h3 className={styles.stepHeading}>Share Your Requirements</h3>
              <p className={styles.stepDisc}>Fill out a brief, targeted questionnaire about your budget, usage, preferences, and must-have features before your call.</p>
            </div>
          </div>

          <div className={styles.stepCard}>
            <div className={styles.stepNo}>03</div>
            <div className={styles.stepContent}>
              <h3 className={styles.stepHeading}>Get Expert Guidance</h3>
              <p className={styles.stepDisc}>Two focused 15-minute video/audio sessions with Faisal, plus one complimentary follow-up call within 30 days.</p>
            </div>
          </div>
        </div>
      </div>

      {/* ─── MEET YOUR EXPERT SECTION ─── */}
      <div ref={expertRef} className={styles.revealUp}>
        <div className={styles.titleWrapper}>
          <h2 className={styles.titleText}>Meet Your Expert</h2>
        </div>

        <div className={styles.consultant}>
          <div className={styles.consultantInfo}>
            <h2 className={styles.consultantName}>Faisal Khan</h2>
            <h4 className={styles.consultantTitle}>Lead Automotive Consultant & CEO</h4>

            <div className={styles.consultantDetails}>
              <p>With 18 years in the automotive industry, Faisal has helped thousands of Indians find their perfect car. His deep understanding of the Indian market, combined with hands-on experience across segments—from hatchbacks to luxury SUVs—makes him India's most trusted independent car advisor.</p>

              <p>What sets Faisal apart is his genuine passion for automobiles and unwavering commitment to consumer advocacy. Unlike influencers who prioritize sponsorships or salespeople driven by commissions, Faisal's advice stems from years of real-world testing, market analysis, and staying updated with every launch, facelift, and technology trend in the Indian automotive landscape.</p>

              <blockquote className={styles.quote}>
                "Choosing the right car can transform your motoring experience."
              </blockquote>
            </div>

            <a href="https://www.caradvice.in/consultancy/" target='_blank' rel="noopener noreferrer" className={styles.ctaBtn}>
              <span className={styles.btnText}>Book Your Session Now</span>
              <span className={styles.btnIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </span>
            </a>
          </div>

          <div className={styles.consultantPicWrapper}>
            <div className={styles.ConsultantPic}>
              <img src={Consultant} alt="Faisal Khan - Automotive Consultant" loading="lazy" />
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Consultancypage;